import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Box,
	Button,
	Paper,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import {
	CheckCircle2,
	ChevronRight,
	Clock,
	FileCheck2,
	History,
	Star,
} from "lucide-react";
import { getRecentActivity } from "utils/supabase";

const ICON_BY_TYPE = {
	exam: CheckCircle2,
	practice: FileCheck2,
	flashcards: Star,
};

const COLOR_BY_TYPE = {
	exam: {
		text: "#7c3aed",
		softBg: "#f3e8ff",
		badgeBg: "#f5f0ff",
	},
	practice: {
		text: "#2563eb",
		softBg: "#dbeafe",
		badgeBg: "#eff6ff",
	},
	flashcards: {
		text: "#f97316",
		softBg: "#ffedd5",
		badgeBg: "#fff7ed",
	},
	default: {
		text: "#4b5563",
		softBg: "#f3f4f6",
		badgeBg: "#f9fafb",
	},
};

function isActivityCompleted(item) {
	const status = String(item?.status || "").toLowerCase();
	const route = String(item?.route || "").toLowerCase();

	return Boolean(
		item?.is_completed ||
		item?.completed_at ||
		item?.finished_at ||
		item?.ended_at ||
		status === "completed" ||
		status === "finished" ||
		status === "finalizada" ||
		status === "finalizado" ||
		route.includes("/resultados")
	);
}

function getActivitySubtitle(item) {
	return item?.subtitle || item?.status_label || "Sin detalles";
}

function getActivityTitle(item) {
	const type = String(item?.type || "").toLowerCase();

	if (type.includes("flashcard") && isActivityCompleted(item)) {
		return "Flashcards - Sesion completada";
	}

	return item?.title || "Actividad";
}

function normalizeRoute(item) {
	const route = item?.route || "";
	const entityId = item?.entity_id;
	const type = item?.type;
	const isCompleted = isActivityCompleted(item);

	if (type === "exam" && entityId) {
		return isCompleted
			? `/admin/simulador/${entityId}/resultados`
			: `/admin/simulador/${entityId}`;
	}

	if (type === "flashcards" && entityId) {
		return isCompleted
			? `/admin/flashcards/${entityId}/resultados`
			: `/admin/flashcards/${entityId}`;
	}

	if (route.startsWith("/admin/")) return route;

	if (type === "practice" && entityId) return `/admin/practica/${entityId}`;

	if (route.startsWith("/")) {
		return route.startsWith("/admin") ? route : `/admin${route}`;
	}

	return "/admin/dashboard";
}

function ActivityRow({ item, onNavigate, isLast }) {
	const Icon = ICON_BY_TYPE[item?.type] || CheckCircle2;
	const palette = COLOR_BY_TYPE[item?.type] || COLOR_BY_TYPE.default;
	const title = getActivityTitle(item);
	const subtitle = getActivitySubtitle(item);
	const rawScore = Number(item?.score_percentage);
	const hasScore = Number.isFinite(rawScore);
	const safeScore = hasScore ? `${Math.round(rawScore)}%` : "--";

	return (
		<Box
			onClick={() => onNavigate(item)}
			sx={{
				width: "100%",
				borderRadius: "14px",
				px: { xs: 1.1, sm: 1.4 },
				py: { xs: 0.9, sm: 1.2 },
				cursor: "pointer",
				borderBottom: isLast ? "none" : "1px solid #f1f5f9",
				transition: "background-color .2s ease, transform .2s ease",
				"&:hover": {
					backgroundColor: "#f8fafc",
					transform: "translateY(-1px)",
				},
			}}
		>
			<Box
				sx={{
					display: "grid",
						gridTemplateColumns: { xs: "44px minmax(0, 1fr) 68px", sm: "56px 1fr 92px" },
					alignItems: "center",
					gap: { xs: 0.8, sm: 1.2 },
				}}
			>
				<Box
					sx={{
						width: { xs: 38, sm: 44 },
						height: { xs: 38, sm: 44 },
						borderRadius: "9999px",
						backgroundColor: palette.softBg,
						color: palette.text,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Icon size={22} strokeWidth={2.2} />
				</Box>

				<Box sx={{ minWidth: 0 }}>
					<Typography
						sx={{
							color: "#0f172a",
							fontSize: { xs: "0.84rem", sm: "0.96rem" },
							fontWeight: 500,
							lineHeight: 1.15,
						}}
					>
						{title}
					</Typography>
					<Typography
						sx={{
							mt: 0.2,
							color: "#64748b",
							fontSize: { xs: "0.74rem", sm: "0.8rem" },
							fontWeight: 400,
							lineHeight: 1.2,
						}}
					>
						{subtitle}
					</Typography>
				</Box>

				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
					}}
				>
					<Box
						sx={{
							minWidth: { xs: 68, sm: 78 },
							borderRadius: "10px",
							px: { xs: 1.1, sm: 1.3 },
							py: { xs: 0.5, sm: 0.7 },
							textAlign: "center",
							color: palette.text,
							backgroundColor: palette.badgeBg,
							fontSize: { xs: "0.82rem", sm: "0.9rem" },
							fontWeight: 600,
							lineHeight: 1,
						}}
					>
						{safeScore}
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

export default function RecentActivity() {
	const history = useHistory();
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let mounted = true;

		async function fetchRecentActivity() {
			setIsLoading(true);

			try {
				const { data, error } = await getRecentActivity();

				if (!mounted) return;
				if (error) {
					console.error("Error fetching recent activity:", error);
					setItems([]);
					return;
				}

				setItems(Array.isArray(data) ? data : []);
			} catch (error) {
				if (!mounted) return;
				console.error("Unexpected error fetching recent activity:", error);
				setItems([]);
			} finally {
				if (mounted) setIsLoading(false);
			}
		}

		fetchRecentActivity();

		return () => {
			mounted = false;
		};
	}, []);

	const emptyStateText = useMemo(
		() => "Aun no hay actividad reciente para mostrar.",
		[]
	);

	const handleNavigate = (item) => {
		const targetRoute = normalizeRoute(item);
		history.push(targetRoute);
	};

	return (
		<Paper
			elevation={0}
			sx={{
				border: "1px solid #f1f5f9",
				borderRadius: "12px",
				p: { xs: 1.5, sm: 2 },
				backgroundColor: "#ffffff",
				boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 0.6,
					mb: { xs: 1, sm: 1.4 },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
					<Box
						sx={{
							width: 40,
							height: 40,
							flexShrink: 0,
							borderRadius: "12px",
							backgroundColor: "#eef2ff",
							color: "#4f46e5",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<History size={20} />
					</Box>
					<Box>
						<Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a", lineHeight: 1.2 }}>
							Actividad reciente
						</Typography>
						<Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", lineHeight: 1.2, mt: 0.15 }}>
							Tus últimas sesiones y resultados
						</Typography>
					</Box>
				</Box>

				<Button
					variant="text"
					endIcon={<ChevronRight size={16} />}
					onClick={() => history.push("/admin/actividad")}
					sx={{
						textTransform: "none",
						color: "#2563eb",
						fontWeight: 600,
						fontSize: { xs: "0.74rem", sm: "0.8rem" },
						minWidth: "auto",
						px: 0.25,
					}}
				>
					Ver historial
				</Button>
			</Box>

			{isLoading ? (
				<Stack spacing={0.6}>
					{Array.from({ length: 4 }).map((_, index) => (
						<Box
							key={`recent-activity-skeleton-${index}`}
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "44px minmax(0, 1fr) 68px", sm: "56px 1fr 92px" },
								alignItems: "center",
								gap: { xs: 0.8, sm: 1.2 },
								px: { xs: 1.1, sm: 1.4 },
								py: { xs: 0.9, sm: 1.2 },
							}}
						>
							<Skeleton variant="circular" width={40} height={40} animation="wave" />
							<Box sx={{ minWidth: 0 }}>
								<Skeleton variant="text" width="70%" height={20} animation="wave" />
								<Skeleton variant="text" width="45%" height={16} animation="wave" />
							</Box>
							<Skeleton variant="rounded" width={64} height={28} animation="wave" sx={{ ml: "auto" }} />
						</Box>
					))}
				</Stack>
			) : items.length === 0 ? (
				<Box
					sx={{
						minHeight: 140,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 0.8,
					}}
				>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "9999px",
							backgroundColor: "#f1f5f9",
							color: "#94a3b8",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Clock size={20} />
					</Box>
					<Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 400 }}>{emptyStateText}</Typography>
				</Box>
			) : (
				<Stack spacing={0}>
					{items.map((item, index) => (
						<ActivityRow
							key={item?.id || `${item?.type}-${item?.entity_id}`}
							item={item}
							onNavigate={handleNavigate}
							isLast={index === items.length - 1}
						/>
					))}
				</Stack>
			)}
		</Paper>
	);
}
